import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, AlertCircle, CheckCircle, Play, BarChart3, Ticket, MessageSquare, AlertTriangle } from 'lucide-react';
import { OnboardingProject, OnboardingTask } from './types';
import { TaskDrawer } from './TaskDrawer';
import { EnhancedGanttChart } from '../ProjectManagement/EnhancedGanttChart';
import { HubTask } from '../../../api/onboardingClient';

interface TimelineViewProps {
  project: OnboardingProject;
  tasks: OnboardingTask[];
  onTaskUpdate?: (taskId: string, updates: any) => void;
  projectId?: string;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  project,
  tasks,
  onTaskUpdate,
  projectId
}) => {
  const [selectedTask, setSelectedTask] = useState<HubTask | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'timeline' | 'gantt' | 'support'>('timeline');
  const [showSupportPanel, setShowSupportPanel] = useState(false);
  const [featureFlags] = useState({ FEATURE_SHEEN: true });

  const phases = [
    { name: 'Kickoff', color: 'bg-blue-500', tasks: tasks.filter(t => t.type === 'setup') },
    { name: 'Data', color: 'bg-purple-500', tasks: tasks.filter(t => t.tags.includes('data')) },
    { name: 'Config', color: 'bg-orange-500', tasks: tasks.filter(t => t.tags.includes('config')) },
    { name: 'Training', color: 'bg-green-500', tasks: tasks.filter(t => t.type === 'training') },
    { name: 'Go-Live', color: 'bg-red-500', tasks: tasks.filter(t => t.tags.includes('golive')) }
  ];

  const convertToHubTask = (task: OnboardingTask): HubTask => ({
    id: task.id,
    title: task.title,
    phase: task.type,
    estHours: task.estimatedHours,
    ownerId: task.assignee,
    due: task.dueDate,
    deps: task.dependencies,
    status: task.status === 'completed' ? 'done' : 
           task.status === 'in-progress' ? 'doing' : 
           task.status === 'blocked' ? 'blocked' : 'todo',
    automations: task.tags,
  });

  const getPhaseDescription = (phaseName: string): string => {
    const descriptions = {
      'Kickoff': 'Project initiation and team alignment completed.',
      'Data': 'Data migration and validation in progress.',
      'Config': 'System configuration and customization.',
      'Training': 'User training and documentation.',
      'Go-Live': 'Final deployment and go-live activities.'
    };
    return descriptions[phaseName] || '';
  };

  const getPhaseStatus = (tasks: OnboardingTask[]): 'completed' | 'in-progress' | 'pending' | 'overdue' => {
    if (tasks.length === 0) return 'pending';
    if (tasks.every(t => t.status === 'completed')) return 'completed';
    if (tasks.some(t => t.status === 'overdue')) return 'overdue';
    if (tasks.some(t => t.status === 'in-progress')) return 'in-progress';
    return 'pending';
  };

  const calculatePhaseProgress = (tasks: OnboardingTask[]): number => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  const getPhaseStartDate = (tasks: OnboardingTask[]): string => {
    if (tasks.length === 0) return new Date().toLocaleDateString();
    const dates = tasks.map(t => new Date(t.dueDate || new Date()));
    return new Date(Math.min(...dates.map(d => d.getTime()))).toLocaleDateString();
  };

  const getPhaseEndDate = (tasks: OnboardingTask[]): string => {
    if (tasks.length === 0) return new Date().toLocaleDateString();
    const dates = tasks.map(t => new Date(t.dueDate || new Date()));
    return new Date(Math.max(...dates.map(d => d.getTime()))).toLocaleDateString();
  };

  const getNextActions = (tasks: OnboardingTask[]) => {
    return tasks
      .filter(t => t.status === 'overdue' || t.status === 'in-progress')
      .slice(0, 5)
      .map(task => ({
        id: task.id,
        title: task.title,
        description: task.description || `${task.type} task`,
        assignee: task.assignee || 'Unassigned',
        dueDate: new Date(task.dueDate || new Date()).toLocaleDateString(),
        priority: task.status === 'overdue' ? 'critical' as const : 'high' as const
      }));
  };

  // Convert OnboardingTasks to EnhancedPlanStep format for Gantt Chart
  const ganttSteps = useMemo(() => {
    return tasks.map((task, index) => ({
      id: task.id,
      name: task.title,
      description: task.description || `${task.type} task for ${project.name}`,
      type: (task.type === 'setup' ? 'analysis' : 
             task.type === 'training' ? 'validation' : 
             task.type === 'review' ? 'validation' : 
             task.type === 'approval' ? 'validation' : 
             'implementation') as 'analysis' | 'implementation' | 'validation' | 'deployment',
      estimatedDuration: task.estimatedHours || 5,
      dependencies: task.dependencies || [],
      resources: [task.assignee || 'Unassigned'],
      successCriteria: task.tags?.length > 0 ? task.tags : ['Task completed successfully'],
      rollbackPlan: `Revert ${task.title} to previous state if issues arise`,
      priority: (task.status === 'overdue' ? 'critical' : 
                task.status === 'in-progress' ? 'high' : 
                task.status === 'completed' ? 'low' : 'medium') as 'low' | 'medium' | 'high' | 'critical',
      wbsNumber: `${index + 1}.0`,
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    }));
  }, [tasks, project.name]);

  const demoGanttSteps = useMemo(() => [
    {
      id: 'gantt-demo-1',
      name: 'Project Kickoff & Planning',
      description: 'Initial project setup and stakeholder alignment',
      type: 'analysis' as const,
      estimatedDuration: 3,
      dependencies: [],
      resources: ['Project Manager', 'Business Analyst'],
      successCriteria: ['Project Charter approved', 'Stakeholder alignment confirmed', 'Initial requirements documented'],
      rollbackPlan: 'Return to pre-project state and reassess scope',
      priority: 'high' as const,
      wbsNumber: '1.0',
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    },
    {
      id: 'gantt-demo-2',
      name: 'Tyler CRM Data Migration',
      description: 'Migrate existing customer data to Tyler CRM system',
      type: 'implementation' as const,
      estimatedDuration: 10,
      dependencies: ['gantt-demo-1'],
      resources: ['Data Engineer', 'Tyler Specialist'],
      successCriteria: ['Data mapping complete', 'Migration scripts tested', 'Data validation passed'],
      rollbackPlan: 'Restore from backup and revert to legacy system',
      priority: 'critical' as const,
      wbsNumber: '2.0',
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    },
    {
      id: 'gantt-demo-3',
      name: 'System Configuration',
      description: 'Configure Tyler system for client requirements',
      type: 'implementation' as const,
      estimatedDuration: 7,
      dependencies: ['gantt-demo-2'],
      resources: ['System Administrator', 'Tyler Consultant'],
      successCriteria: ['Configuration complete', 'System tests passed', 'Performance benchmarks met'],
      rollbackPlan: 'Revert to default configuration settings',
      priority: 'high' as const,
      wbsNumber: '3.0',
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    },
    {
      id: 'gantt-demo-4',
      name: 'User Training & Documentation',
      description: 'Train end users and create documentation',
      type: 'validation' as const,
      estimatedDuration: 5,
      dependencies: ['gantt-demo-3'],
      resources: ['Training Specialist', 'Technical Writer'],
      successCriteria: ['Training materials complete', 'User training sessions conducted', 'Documentation approved'],
      rollbackPlan: 'Provide additional training sessions and support',
      priority: 'medium' as const,
      wbsNumber: '4.0',
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    },
    {
      id: 'gantt-demo-5',
      name: 'Go-Live & Support',
      description: 'System go-live and initial support period',
      type: 'deployment' as const,
      estimatedDuration: 3,
      dependencies: ['gantt-demo-4'],
      resources: ['Project Manager', 'Support Team'],
      successCriteria: ['System successfully deployed', 'Users actively using system', 'Support processes established'],
      rollbackPlan: 'Emergency rollback to previous system if critical issues arise',
      priority: 'critical' as const,
      wbsNumber: '5.0',
      parentId: undefined,
      children: [],
      level: 0,
      isCollapsed: false
    }
  ], []);

  const ganttDependencies = useMemo(() => [
    { from: 'gantt-demo-1', to: 'gantt-demo-2', type: 'finish-to-start' as const },
    { from: 'gantt-demo-2', to: 'gantt-demo-3', type: 'finish-to-start' as const },
    { from: 'gantt-demo-3', to: 'gantt-demo-4', type: 'finish-to-start' as const },
    { from: 'gantt-demo-4', to: 'gantt-demo-5', type: 'finish-to-start' as const }
  ], []);

  const criticalPath = useMemo(() => [
    'gantt-demo-1', 'gantt-demo-2', 'gantt-demo-3', 'gantt-demo-5'
  ], []);

  const handleTaskClick = (task: OnboardingTask) => {
    setSelectedTask(convertToHubTask(task));
    setIsDrawerOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in-progress': return <Play className="w-4 h-4 text-blue-500" />;
      case 'overdue': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Calendar className="w-6 h-6 text-[#4267A1] mr-3" />
              Project Timeline
            </h2>
          
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
              {[
                { key: 'timeline', label: 'Timeline', icon: Calendar },
                { key: 'gantt', label: 'Gantt', icon: BarChart3 },
                { key: 'support', label: 'Support', icon: Ticket }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key as any)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    viewMode === key 
                      ? 'bg-[#4267A1] text-white shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        
        </div>

        {viewMode === 'timeline' && (
          <div className="flex gap-6">
            <div className="flex-1 space-y-6">
              {phases.map((phase) => {
                const phaseData = {
                  id: phase.name.toLowerCase(),
                  name: phase.name,
                  description: getPhaseDescription(phase.name),
                  status: getPhaseStatus(phase.tasks) as 'completed' | 'in-progress' | 'pending' | 'overdue',
                  progress: calculatePhaseProgress(phase.tasks),
                  startDate: getPhaseStartDate(phase.tasks),
                  endDate: getPhaseEndDate(phase.tasks),
                  tasks: phase.tasks.map(task => ({
                    id: task.id,
                    title: task.title,
                    status: task.status as 'completed' | 'in-progress' | 'pending' | 'overdue',
                    assignee: task.assignee || 'Unassigned'
                  }))
                };
                
                return (
                  <div key={phase.name} className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{phase.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{phaseData.description}</p>
                    <div className="space-y-2">
                      {phaseData.tasks.map(task => (
                        <div key={task.id} className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                             onClick={() => {
                               const originalTask = tasks.find(t => t.id === task.id);
                               if (originalTask) handleTaskClick(originalTask);
                             }}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{task.title}</span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              task.status === 'completed' ? 'bg-green-100 text-green-800' :
                              task.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                              task.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="w-80">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Actions</h3>
                <div className="space-y-3">
                  {getNextActions(tasks).map(action => (
                    <div key={action.id} className="p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100"
                         onClick={() => {
                           const task = tasks.find(t => t.id === action.id);
                           if (task) handleTaskClick(task);
                         }}>
                      <div className="font-medium text-blue-900">{action.title}</div>
                      <div className="text-sm text-blue-700">{action.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'gantt' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <BarChart3 className="w-6 h-6 text-[#4267A1] mr-3" />
                  Project Gantt Chart
                </h3>
                <button
                  onClick={() => setShowSupportPanel(true)}
                  className="px-4 py-2 bg-[#4267A1] text-white rounded-lg font-medium hover:bg-[#3B5998] transition-colors flex items-center"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Open Support
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between text-[#4267A1]">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#4267A1] rounded-full"></div>
                      <span className="font-medium">Project: {project.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Tasks: {ganttSteps.length > 0 ? ganttSteps.length : demoGanttSteps.length}</span>
                    </div>
                  </div>
                  {ganttSteps.length === 0 && (
                    <span className="text-sm bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-medium">
                      Demo Mode
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" style={{ height: '600px' }}>
              <div className="h-full">
                <EnhancedGanttChart
                  steps={ganttSteps.length > 0 ? ganttSteps : demoGanttSteps}
                  dependencies={ganttDependencies}
                  criticalPath={criticalPath}
                  selectedStepId={selectedTask?.id}
                  onStepSelect={(stepId) => {
                    const task = tasks.find(t => t.id === stepId);
                    if (task) {
                      handleTaskClick(task);
                    }
                  }}
                  onStepUpdate={(stepId, updates) => {
                    onTaskUpdate?.(stepId, updates);
                  }}
                  readonly={false}
                  showBaseline={false}
                />
              </div>
            </div>
          </div>
        )}

        {viewMode === 'support' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Ticket className="w-6 h-6 text-[#4267A1] mr-3" />
                Client Support Portal
              </h3>
              <div className="flex items-center space-x-3">
                <button className="px-4 py-2 bg-[#4267A1] text-white rounded-lg font-medium hover:bg-[#3B5998] transition-colors flex items-center">
                  <Ticket className="w-4 h-4 mr-2" />
                  Create Ticket
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Knowledge Base
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
                  Active Support Tickets
                </h4>
                <div className="space-y-3">
                  <div className="p-4 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">#SB-2024-001</span>
                      <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">High Priority</span>
                    </div>
                    <p className="text-gray-700 text-sm">SSO configuration blocked - needs immediate attention</p>
                    <p className="text-gray-500 text-xs mt-2">Created 2 hours ago • Assigned to Support Team</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">#SB-2024-002</span>
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">In Progress</span>
                    </div>
                    <p className="text-gray-700 text-sm">Data migration questions from client team</p>
                    <p className="text-gray-500 text-xs mt-2">Created yesterday • Assigned to Technical Lead</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <MessageSquare className="w-5 h-5 text-[#4267A1] mr-2" />
                  Communication Hub
                </h4>
                <div className="space-y-3">
                  <div className="p-4 bg-white rounded-lg border border-green-200">
                    <p className="text-gray-700 text-sm mb-2">
                      "Training session scheduled for next week. Please confirm attendance."
                    </p>
                    <p className="text-gray-500 text-xs">Project Manager • 1 hour ago</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-blue-200">
                    <p className="text-gray-700 text-sm mb-2">
                      "Sample data validation completed successfully. Ready for next phase."
                    </p>
                    <p className="text-gray-500 text-xs">System Bot • 3 hours ago</p>
                  </div>
                </div>
                <button className="w-full mt-4 px-4 py-2 bg-[#4267A1] text-white rounded-lg font-medium hover:bg-[#3B5998] transition-colors">
                  Ask a Question
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-500">Done ({tasks.filter(t => t.status === 'completed').length})</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-blue-500">On Track ({tasks.filter(t => t.status === 'in-progress').length})</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">Overdue ({tasks.filter(t => t.status === 'overdue').length})</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-500">At Risk ({tasks.filter(t => t.status === 'pending').length})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TaskDrawer
        task={selectedTask}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onMarkDone={(taskId) => {
          onTaskUpdate?.(taskId, { status: 'completed', completionPercentage: 100 });
          setIsDrawerOpen(false);
        }}
        onReassign={(taskId, userId) => {
          onTaskUpdate?.(taskId, { assignee: userId });
        }}
        onEscalate={(taskId) => {
          console.log('Escalating task:', taskId);
          setIsDrawerOpen(false);
        }}
        onAddNote={(taskId, note) => {
          console.log('Adding note to task:', taskId, note);
        }}
      />

    </>
  );
};

export default TimelineView;
